import React, { useState, useEffect, useContext } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FiMapPin, FiCamera, FiStar, FiHeart, FiPhone } from 'react-icons/fi';
import { AuthContext } from '../context/AuthContext';
import { generateSlug } from '../utils/slugify';
import { getImageUrl } from '../utils/imageUtils';

export const generateWealthTitle = (property) => {
  if (property.seoTitle) return property.seoTitle;
  if (property.title) return property.title;
  return property.propertyType ? `${property.propertyType} ${property.location ? `in ${property.location.split(',')[0]}` : ''}` : 'Featured Property';
};

const FeaturedPropertyCard = ({ property, layout = 'vertical' }) => {
  const [currentImgIndex, setCurrentImgIndex] = useState(0);
  const navigate = useNavigate();
  const { user, userFavorites, toggleFavorite } = useContext(AuthContext);

  const getImages = () => {
    if (property.photo) {
      const photos = Array.isArray(property.photo) ? property.photo : [property.photo];
      const isWealth = property.isWealthProperty || property.isWealthProject || property.photo.includes('valueProjects');
      return photos.map(p => property.newImageUrl || (p.startsWith('http') ? p : getImageUrl(p, isWealth)));
    }
    if (property.images && property.images.length > 0) {
      return property.images.map(img => getImageUrl(img));
    }
    return ['https://images.unsplash.com/photo-1522708323590-d24dbb6b0267?w=800&q=80'];
  };

  const images = getImages();
  const imageUrl = images[currentImgIndex] || images[0];
  const imageCount = images.length;
  
  const title = generateWealthTitle(property);
  const price = property.price ? property.price.toLocaleString('en-IN') : 'On Request';
  const slug = generateSlug(title);

  const isFavorite = userFavorites?.some(fav => 
    typeof fav === 'object' ? fav._id === property._id : fav === property._id
  );

  const handleFavoriteClick = async (e) => {
    e.stopPropagation();
    if (!user) {
      navigate('/login');
      return;
    }
    await toggleFavorite(property._id);
  };

  const handleContactClick = (e) => {
    e.stopPropagation();
    navigate(`/wealth-property/${slug}/${property._id}`);
  };

  const formatPrice = (p) => {
    if (!p) return 'On Request';
    const num = Number(p);
    if (num >= 10000000) return `₹ ${(num / 10000000).toFixed(2)} Cr`;
    if (num >= 100000) return `₹ ${(num / 100000).toFixed(1)} L`;
    return `₹ ${num.toLocaleString('en-IN')}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '';
    
    const diffTime = Math.abs(new Date() - date);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 1) return 'Posted today';
    if (diffDays === 2) return 'Posted yesterday';
    if (diffDays <= 7) return `Posted ${diffDays - 1} days ago`;
    
    return `Posted on ${date.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}`;
  };

  if (layout === 'horizontal') {
    return (
      <div 
        className="bg-white rounded-xl border border-gray-200 shadow-md cursor-pointer hover:shadow-xl transition-all duration-300 relative group flex flex-col md:flex-row overflow-hidden w-full"
        onClick={handleContactClick}
      >
        {/* Left Side: Image Slider */}
        <div className="relative w-full md:w-[320px] h-[220px] md:h-auto flex-shrink-0 overflow-hidden">
          <div className="absolute top-2 left-2 z-10 flex gap-2 flex-col items-start">
            <span className="bg-primary text-white text-[10px] font-bold px-2 py-1 rounded shadow-sm uppercase tracking-wider flex items-center gap-1">
              <FiStar size={10} className="fill-white" /> Featured
            </span>
          </div>
          
          <button 
            onClick={handleFavoriteClick}
            className="absolute z-10 w-8 h-8 bg-white/90 rounded-full flex items-center justify-center shadow hover:bg-white transition-colors"
            style={{ top: '10px', right: '10px' }}
          >
            <FiHeart className={isFavorite ? "text-red-500 fill-red-500" : "text-gray-400 hover:text-red-500"} />
          </button>

          <img 
            src={imageUrl} 
            alt={property.propertyType} 
            className="absolute inset-0 w-full h-full object-cover transition duration-500 group-hover:scale-105" 
          />
          {(!property.images || property.images.length === 0) && !property.coverImage && !property.photo && (
            <div className="absolute inset-0 bg-black/40 flex flex-col items-center justify-center z-20">
              <button 
                onClick={(e) => { e.stopPropagation(); handleContactClick(e); }} 
                className="bg-white text-primary px-3 py-1.5 rounded font-bold text-xs md:text-sm shadow-lg flex items-center gap-1.5 hover:bg-gray-100 transition-colors"
              >
                <FiCamera /> Request Image
              </button>
            </div>
          )}
          
          <div className="absolute bottom-2 right-2 z-10 bg-black/60 text-white text-[10px] font-medium px-2 py-1 rounded flex items-center gap-1">
            <FiCamera size={12} /> {imageCount}
          </div>
        </div>
        
        {/* Right Side: Content */}
        <div className="p-4 md:p-5 flex flex-col flex-grow bg-orange-50/30">
          <div className="mb-3">
            <div className="text-xs font-bold text-primary mb-1 uppercase tracking-wider">FOR SALE</div>
            <h3 className="font-bold text-lg md:text-xl text-gray-900 mb-1 hover:text-primary transition-colors" title={title}>
              <Link to={`/wealth-property/${slug}/${property._id}`} className="block truncate">{title}</Link>
            </h3>
            <p className="text-sm text-gray-500 flex items-center gap-1 overflow-hidden">
              <FiMapPin size={14} className="flex-shrink-0" /> 
              <span className="block truncate">
                {property.location || 'Location upon request'}
              </span>
            </p>
          </div>

          <div className="flex flex-wrap gap-x-8 gap-y-2 mb-4">
            <div>
              <p className="text-xs text-gray-500 font-medium mb-0.5">Price</p>
              <div className="font-bold text-lg text-gray-900">
                ₹{price}
              </div>
            </div>
            
            {property.plotArea && (
              <div>
                <p className="text-xs text-gray-500 font-medium mb-0.5">Area</p>
                <div className="font-semibold text-[15px] text-gray-800">
                  {property.plotArea}
                </div>
              </div>
            )}

            {property.propertyType && (
              <div>
                <p className="text-xs text-gray-500 font-medium mb-0.5">Property Type</p>
                <div className="font-semibold text-[15px] text-gray-800 uppercase">
                  {property.propertyType}
                </div>
              </div>
            )}
          </div>

          <div className="mb-4 flex-grow flex gap-2 flex-wrap">
             {property.ventureName && <span className="bg-white border border-gray-200 text-gray-600 text-[10px] font-medium px-2 py-1 rounded">ventureName: {property.ventureName}</span>}
             {property.facing && <span className="bg-white border border-gray-200 text-gray-600 text-[10px] font-medium px-2 py-1 rounded">{property.facing}</span>}
             {property.approvalType && <span className="bg-white border border-gray-200 text-gray-600 text-[10px] font-medium px-2 py-1 rounded">{property.approvalType}</span>}
          </div>

          <div className="mt-auto pt-4 border-t border-gray-200 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="bg-primary/10 text-primary flex items-center justify-center font-bold text-xs rounded-full w-8 h-8 flex-shrink-0">
                W
              </div>
              <div>
                <span className="text-sm font-semibold text-gray-900 block truncate max-w-[120px] md:max-w-[200px]">
                  Wealth Associates
                </span>
                <div className="text-[10px] text-gray-500 flex items-center gap-1">
                  <span>Verified Dealer</span>
                </div>
              </div>
            </div>
            
            <div className="flex gap-2">
              <button onClick={handleContactClick} className="px-4 py-2 text-sm font-semibold bg-primary text-white rounded-lg flex items-center gap-1.5 hover:bg-orange-600 transition-colors shadow-sm">
                <FiPhone size={14} /> Contact
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Vertical Layout (Rich Grid Style)
  return (
    <div 
      className="bg-white rounded-xl border border-gray-200 shadow-md cursor-pointer hover:shadow-xl transition-all duration-300 relative group flex flex-col overflow-hidden w-full h-full"
      onClick={handleContactClick}
    >
      {/* Top Side: Image Area */}
      <div className="relative w-full h-[240px] flex-shrink-0 bg-gray-100 overflow-hidden">
        <div className="absolute top-2 left-2 z-10 flex gap-2 flex-col items-start">
          <span className="bg-primary text-white text-[10px] font-bold px-2 py-1 rounded shadow-sm uppercase tracking-wider flex items-center gap-1">
            <FiStar size={10} className="fill-white" /> Featured
          </span>
        </div>
        
        <button 
          onClick={handleFavoriteClick}
          className="absolute z-10 w-8 h-8 bg-white/90 rounded-full flex items-center justify-center shadow hover:bg-white transition-colors"
          style={{ top: '10px', right: '10px' }}
        >
          <FiHeart className={isFavorite ? "text-red-500 fill-red-500" : "text-gray-400 hover:text-red-500"} />
        </button>

        <img 
          src={imageUrl} 
          alt={property.propertyType} 
          className="w-full h-full object-cover transition duration-500 group-hover:scale-105" 
        />
        {(!property.images || property.images.length === 0) && !property.coverImage && !property.photo && (
          <div className="absolute inset-0 bg-black/40 flex flex-col items-center justify-center z-20">
            <button 
              onClick={(e) => { e.stopPropagation(); handleContactClick(e); }} 
              className="bg-white text-primary px-3 py-1.5 rounded font-bold text-xs md:text-sm shadow-lg flex items-center gap-1.5 hover:bg-gray-100 transition-colors"
            >
              <FiCamera /> Request Image
            </button>
          </div>
        )}
        
        <div className="absolute bottom-2 right-2 z-10 bg-black/60 text-white text-[10px] font-medium px-2 py-1 rounded flex items-center gap-1">
          <FiCamera size={12} /> {imageCount}
        </div>
      </div>

      {/* Bottom Side: Content */}
      <div className="p-4 flex flex-col flex-grow bg-orange-50/10">
        <div className="mb-3">
          <h3 className="font-bold text-lg md:text-xl text-gray-900 mb-1 hover:text-primary transition-colors" title={title}>
            <Link to={`/wealth-property/${slug}/${property._id}`} className="block truncate">{title}</Link>
          </h3>
          <p className="text-sm text-gray-500 flex items-center gap-1 overflow-hidden">
            <FiMapPin size={14} className="flex-shrink-0" /> 
            <span className="block truncate">
              {property.location?.split(',')[0] || 'Prime Location'}
              {property.location && property.location.includes(',') ? `, ${property.location.split(',').slice(1).join(',').trim()}` : ''}
            </span>
          </p>
        </div>

        <div className="mb-4">
          <div className="flex items-center gap-2 flex-wrap mb-3 text-[15px]">
            {property.area && <span className="text-primary font-bold">{property.area} Sq Yards</span>}
            {property.area && <span className="text-gray-400">-</span>}
            <span className="text-gray-900 font-bold">{formatPrice(property.price)}</span>
          </div>

          {(property.ventureName || property.facing) && (
            <div className="flex flex-wrap gap-x-8 gap-y-2 pt-2 border-t border-gray-100">
               {property.ventureName && (
                 <div>
                   <p className="text-xs text-gray-500 font-medium mb-0.5">Venture</p>
                   <div className="font-semibold text-[14px] text-gray-800">{property.ventureName}</div>
                 </div>
               )}
               {property.facing && (
                 <div>
                   <p className="text-xs text-gray-500 font-medium mb-0.5">Facing</p>
                   <div className="font-semibold text-[14px] text-gray-800">{property.facing}</div>
                 </div>
               )}
            </div>
          )}
        </div>

        <div className="mt-auto pt-4 border-t border-gray-200 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="bg-primary/10 text-primary flex items-center justify-center font-bold text-xs rounded-full w-8 h-8 flex-shrink-0">
              W
            </div>
            <div>
              <span className="text-sm font-semibold text-gray-900 block truncate max-w-[100px]">
                Wealth Associates
              </span>
              <div className="text-[10px] text-gray-500 flex items-center gap-1">
                <span>Verified Dealer</span>
              </div>
            </div>
          </div>
          
          <div className="flex gap-2">
            <button onClick={handleContactClick} className="px-3 py-1.5 text-xs font-semibold bg-primary text-white rounded flex items-center gap-1.5 hover:bg-orange-600 transition-colors shadow-sm">
              <FiPhone size={12} /> Contact
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FeaturedPropertyCard;
